/**
 * Capture / restore keyboard focus across cart section HTML swaps (innerHTML).
 * Many interactive controls are destroyed and recreated; without this, focus drops to <body>.
 */
(function () {
  function escapeSelectorValue(value) {
    if (value == null) return '';
    const s = String(value);
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(s);
    }
    return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * @param {Element | null | undefined} el
   * @returns {object | null}
   */
  function captureCartFocus(el) {
    if (!el || !(el instanceof Element)) return null;
    if (el === document.body || el === document.documentElement) return null;

    const lineItem = el.closest('li.cart-item, .cart-item[id^="CartItem-"], .cart-item[id^="CartDrawer-Item-"]');
    const lineItemId = lineItem && lineItem.id ? lineItem.id : null;

    const form = 'form' in el && el.form instanceof HTMLFormElement ? el.form : null;
    const formId = form && form.id ? form.id : '';

    const tag = el.tagName;
    const type = el.getAttribute('type');
    const name = el.getAttribute('name');
    const id = el.id || '';
    const value = 'value' in el ? el.value : null;

    const ds = el.dataset || {};
    const dataIndex = ds.index != null ? String(ds.index) : null;
    const dataVId = ds.vId != null ? String(ds.vId) : '';
    const ariaControls = el.getAttribute('aria-controls');

    /** @type {{ name: string, value: string } | null} */
    let pwAttr = null;
    for (let i = 0; el.attributes && i < el.attributes.length; i++) {
      const a = el.attributes[i];
      if (a.name.indexOf('data-pw-') === 0) {
        pwAttr = { name: a.name, value: a.value };
        break;
      }
    }

    return {
      id,
      tag,
      type,
      name,
      value,
      lineItemId,
      dataIndex,
      dataVId,
      ariaControls,
      pwAttr,
      formId,
    };
  }

  /**
   * @param {object | null} snapshot
   * @returns {HTMLElement | null} element that received focus, if any
   */
  function restoreCartFocus(snapshot) {
    if (!snapshot) return null;

    let el = null;

    if (snapshot.id) {
      el = document.getElementById(snapshot.id);
      if (!el) {
        try {
          el = document.querySelector('#' + escapeSelectorValue(snapshot.id));
        } catch (e) {
          el = null;
        }
      }
    }

    if (!el && snapshot.lineItemId && snapshot.name) {
      const li = document.getElementById(snapshot.lineItemId);
      if (li) {
        el = li.querySelector('[name="' + snapshot.name.replace(/"/g, '\\"') + '"]');
      }
    }

    if (!el && snapshot.lineItemId && snapshot.tag === 'INPUT' && snapshot.type === 'checkbox' && snapshot.dataVId) {
      const li = document.getElementById(snapshot.lineItemId);
      if (li) {
        el = li.querySelector('input[type="checkbox"][data-v-id="' + snapshot.dataVId.replace(/"/g, '\\"') + '"]');
      }
    }

    if (!el && snapshot.tag === 'INPUT' && snapshot.type === 'checkbox' && snapshot.dataVId) {
      el = document.querySelector(
        'input[type="checkbox"][data-v-id="' + snapshot.dataVId.replace(/"/g, '\\"') + '"]',
      );
    }

    if (!el && snapshot.dataIndex && snapshot.name && snapshot.tag) {
      const lower = snapshot.tag.toLowerCase();
      el = document.querySelector(
        lower + '[name="' + snapshot.name.replace(/"/g, '\\"') + '"][data-index="' + snapshot.dataIndex + '"]',
      );
    }

    if (!el && snapshot.name && snapshot.tag) {
      const lower = snapshot.tag.toLowerCase();
      try {
        el = document.querySelector(lower + '[name="' + escapeSelectorValue(snapshot.name) + '"]');
      } catch (e) {
        el = document.querySelector(lower + '[name="' + snapshot.name.replace(/"/g, '\\"') + '"]');
      }
    }

    if (!el && snapshot.pwAttr && snapshot.pwAttr.name) {
      const pn = snapshot.pwAttr.name;
      const pv = snapshot.pwAttr.value;
      try {
        el = pv
          ? document.querySelector('[' + pn + '="' + escapeSelectorValue(pv) + '"]')
          : document.querySelector('[' + pn + ']');
      } catch (e) {
        el = document.querySelector('[' + pn + ']');
      }
    }

    if (!el && snapshot.formId) {
      const f = document.getElementById(snapshot.formId);
      if (f) {
        el = f.querySelector('button[type="submit"], input[type="submit"], [type="submit"]');
      }
    }

    if (el && typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true });
        return el;
      } catch (e) {
        return null;
      }
    }

    if (snapshot.lineItemId) {
      const li = document.getElementById(snapshot.lineItemId);
      if (li) {
        const fallback = li.querySelector(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (fallback && typeof fallback.focus === 'function') {
          try {
            fallback.focus({ preventScroll: true });
            return fallback;
          } catch (e) {
            return null;
          }
        }
      }
    }

    return null;
  }

  /**
   * Re-apply focus after section HTML swaps (Expandable / Splide / trapFocus often run slightly later).
   * @param {object | null} snapshot
   */
  function scheduleRestoreCartFocus(snapshot) {
    if (!snapshot) return;
    const run = function () {
      restoreCartFocus(snapshot);
    };
    run();
    requestAnimationFrame(function () {
      run();
      requestAnimationFrame(run);
    });
    setTimeout(run, 0);
    setTimeout(run, 100);
    setTimeout(run, 280);
  }

  window.captureCartFocus = captureCartFocus;
  window.restoreCartFocus = restoreCartFocus;
  window.scheduleRestoreCartFocus = scheduleRestoreCartFocus;
})();
